import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Bell, Paperclip, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, Panel, Empty } from "@/components/app/PageParts";
import { DeadlineDialog } from "@/components/app/DeadlineDialog";
import { PropertyFilter, PropertyTag } from "@/components/app/PropertySelect";
import { api, errorMessage } from "@/lib/api";
import { dateParts, formatDate, reminderStatus } from "@/lib/format";

const DeadlineRow = ({ d, today, onPatch, onDelete, properties }) => {
  const { month, day } = dateParts(d.due_date);
  const overdue = d.status === "open" && d.confirmed && d.due_date < today;
  return (
    <li data-testid={`deadline-row-${d.id}`} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center">
      <div className="flex min-w-0 flex-1 items-start gap-4">
        <div className={`grid h-14 w-14 shrink-0 place-items-center rounded-lg border text-center ${overdue ? "border-red-200 bg-red-50 text-red-700" : "bg-navy text-white border-navy"}`}>
          <span className="text-[10px] uppercase tracking-wider opacity-80">{month}</span>
          <span className="-mt-1 font-display text-xl font-bold">{day}</span>
        </div>
        <div className="min-w-0">
          <p className={`text-sm font-medium ${d.status === "done" ? "text-slate-400 line-through" : "text-navy"}`} data-testid="deadline-title">{d.title}</p>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
            {d.confirmed ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-800" data-testid="deadline-confirmed-badge">Confirmed</span>
              : <span className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-900" data-testid="deadline-unconfirmed-badge">Date not confirmed</span>}
            {overdue && <span className="rounded-full bg-red-50 px-2 py-0.5 text-red-700" data-testid="deadline-overdue-badge">Overdue</span>}
            <PropertyTag properties={properties} id={d.property_id} />
            {d.document_name && <span className="inline-flex items-center gap-1 text-slate-500"><Paperclip className="h-3 w-3" />{d.document_name}{d.document_deleted && <span className="text-red-600"> (deleted)</span>}</span>}
          </div>
          {d.pending_reminder && (
            <p className="mt-1.5 inline-flex items-center gap-1 text-xs text-slate-500" data-testid="deadline-reminder-info">
              <Bell className="h-3 w-3" /> Reminder email on {formatDate(d.pending_reminder.remind_on)} at about 8am.
            </p>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {!d.confirmed && d.status === "open" && <Button size="sm" variant="outline" onClick={() => onPatch(d, { confirmed: true })} data-testid="deadline-confirm-button">Confirm date</Button>}
        <Button size="sm" variant="outline" onClick={() => onPatch(d, { status: d.status === "done" ? "open" : "done" })} data-testid="deadline-toggle-done-button">
          {d.status === "done" ? "Reopen" : "Mark done"}
        </Button>
        <Button size="sm" variant="outline" onClick={() => onDelete(d)} data-testid="deadline-delete-button" className="text-red-700 hover:bg-red-50"><Trash2 className="h-4 w-4" /></Button>
      </div>
    </li>
  );
};

const ReminderList = ({ reminders }) => (
  <Panel title="Reminder log" className="mt-6" testId="reminders-list">
    {!reminders.length ? <Empty testId="reminders-empty">No reminders saved.</Empty> : (
      <ul className="divide-y">
        {reminders.map((r) => (
          <li key={r.id} data-testid={`reminder-row-${r.id}`} className="flex items-center justify-between gap-4 px-5 py-3 text-sm">
            <span className="min-w-0 truncate text-navy">{r.deadline_title} · <span className="text-slate-500">{formatDate(r.remind_on)}</span></span>
            <span data-testid="reminder-status" className={`shrink-0 rounded-full px-2 py-0.5 text-xs ${r.status === "pending" ? "bg-slate-100 text-slate-700" : r.status === "sent" ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"}`}>
              {reminderStatus(r)}
            </span>
          </li>
        ))}
      </ul>
    )}
  </Panel>
);

export default function Deadlines() {
  const { workspace, properties } = useOutletContext();
  const [filter, setFilter] = useState(null);
  const [items, setItems] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [today, setToday] = useState("");
  const base = `/workspaces/${workspace.id}`;

  const load = useCallback(async () => {
    try {
      const [d, r, w] = await Promise.all([api.get(`${base}/deadlines`, { params: { property_id: filter || undefined } }), api.get(`${base}/reminders`), api.get(base)]);
      setItems(d.data.deadlines); setReminders(r.data.reminders); setToday(w.data.workspace.today);
    } catch (e) { toast.error(errorMessage(e)); }
  }, [base, filter]);
  useEffect(() => { load(); }, [load]);

  const patch = async (d, body) => { try { await api.patch(`${base}/deadlines/${d.id}`, body); load(); } catch (e) { toast.error(errorMessage(e)); } };
  const remove = async (d) => { try { await api.delete(`${base}/deadlines/${d.id}`); toast.success("Deadline deleted"); load(); } catch (e) { toast.error(errorMessage(e)); } };

  const open = items?.filter((d) => d.status === "open") || [];
  const groups = [
    ["Overdue", open.filter((d) => d.confirmed && d.due_date < today), "deadlines-overdue-list"],
    ["Upcoming confirmed", open.filter((d) => d.confirmed && d.due_date >= today), "deadlines-upcoming-list"],
    ["Dates to confirm", open.filter((d) => !d.confirmed), "deadlines-unconfirmed-list"],
    ["Done", items?.filter((d) => d.status === "done") || [], "deadlines-done-list"],
  ];
  return (
    <div data-testid="deadlines-page">
      <PageHeader eyebrow="Due dates" title="Deadlines" subtitle="Add the dates your paperwork mentions. Mark each one confirmed once you've checked it."
        action={<DeadlineDialog workspace={workspace} properties={properties} onSaved={load} />} />
      <PropertyFilter properties={properties} value={filter} onChange={setFilter} />
      <div className="space-y-6">
        {groups.map(([title, list, testId]) => (
          (list.length > 0 || title === "Upcoming confirmed") && (
            <Panel key={title} title={`${title} (${list.length})`} testId={testId}>
              {items && !list.length ? <Empty testId={`${testId}-empty`}>No confirmed deadlines coming up.</Empty>
                : <ul className="divide-y">{list.map((d) => <DeadlineRow key={d.id} d={d} today={today} onPatch={patch} onDelete={remove} properties={properties} />)}</ul>}
            </Panel>
          )
        ))}
      </div>
      <ReminderList reminders={reminders} />
    </div>
  );
}
